import React, { useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointAssetsLiabilities";
import { useAddAssetsLiabilities } from "app/hooks/assetsLiabilities/useAddAssetsLiabilities";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";
import RadioButton from "app/components/common/buttons/RadioButton";
import AddMore from "app/components/common/buttons/AddMore";
import { ASSST_LIABILITY } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import { JointAssetsLiabilitiesPartFourFormData } from "app/utils/types";
import DynamicOtherLiabilities from "app/components/Individual/applicant/assetsLiabilities/DynamicOtherLiabilities";
import { useDeleteItem } from "app/hooks/useDeleteItem";

const PartFour: React.FC = () => {
  const dispatch = useDispatch();
  const { mutate: deleteItem, isPending: deleteIsPending } = useDeleteItem();
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps?.savedIds
  );
  const { mutateAsync: submitAssetsLiabilities, isPending } =
    useAddAssetsLiabilities();
  const { formFour, assetLiabilityId, secoundAssetLiabilityId, ...others } =
    useSelector(
      (state: RootState) => state.jointAssetsLiabilities.stepsData
    ) || {
      formFour: { otherLiabilities: [] },
    };

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<JointAssetsLiabilitiesPartFourFormData>({
    defaultValues: {
      yourDetails: {
        otherliabilitiesType: "no",
        otherLiabilities: [
          {
            name: "",
            balanceOwing: "",
            minMonthlyPayment: "",
          },
        ],
      },
      coBorrowerDetails: {
        otherliabilitiesType: "no",
        otherLiabilities: [
          {
            name: "",
            balanceOwing: "",
            minMonthlyPayment: "",
          },
        ],
      },
    },
  });
  const otherliabilitiesValue = watch("yourDetails.otherliabilitiesType");
  const cootherliabilitiesValue = watch(
    "coBorrowerDetails.otherliabilitiesType"
  );

  const onSubmit = async (data: JointAssetsLiabilitiesPartFourFormData) => {
    const { yourDetails, coBorrowerDetails } = data;
    const yourDetailsPayload = {
      otherLiabilities:
        yourDetails.otherliabilitiesType === "yes"
          ? yourDetails.otherLiabilities.map((item) => ({
              ...item,
              minMonthlyPayment: Number(item.minMonthlyPayment),
              balanceOwing: Number(item.balanceOwing),
            }))
          : null,
    };
    const coBorrowerDetailsPayload = {
      otherLiabilities:
        coBorrowerDetails.otherliabilitiesType === "yes"
          ? coBorrowerDetails.otherLiabilities.map((item) => ({
              ...item,
              minMonthlyPayment: Number(item.minMonthlyPayment),
              balanceOwing: Number(item.balanceOwing),
            }))
          : null,
    };
    const request1 = {
      url: `${ASSST_LIABILITY}/${savedIds.applicantID}/fifth`,
      data: yourDetailsPayload,
    };
    const request2 = {
      url: `${ASSST_LIABILITY}/${savedIds.secondApplicantID}/fifth`,
      data: coBorrowerDetailsPayload,
    };
    try {
      const [response1, response2] = await Promise.all([
        submitAssetsLiabilities(request1),
        submitAssetsLiabilities(request2),
      ]);
      if (response1 && response2) {
        const yourOtherLiabilitiesMapped = response1?.otherLiabilities?.map(
          (item: any) => ({
            id: item.id,
            name: item.name,
            balanceOwing: item.balanceOwing,
            minMonthlyPayment: item.minMonthlyPayment,
          })
        );
        const coOtherLiabilitiesMapped = response2?.otherLiabilities?.map(
          (item: any) => ({
            id: item.id,
            name: item.name,
            balanceOwing: item.balanceOwing,
            minMonthlyPayment: item.minMonthlyPayment,
          })
        );

        const savedData = {
          yourDetails: {
            ...formFour?.yourDetails,
            otherLiabilities:
              yourDetails.otherliabilitiesType === "no"
                ? [
                    {
                      name: "",
                      balanceOwing: "",
                      minMonthlyPayment: "",
                    },
                  ]
                : yourOtherLiabilitiesMapped,
            otherliabilitiesType: yourDetails.otherliabilitiesType,
          },
          coBorrowerDetails: {
            ...formFour?.coBorrowerDetails,
            otherLiabilities:
              coBorrowerDetails.otherliabilitiesType === "no"
                ? [
                    {
                      name: "",
                      balanceOwing: "",
                      minMonthlyPayment: "",
                    },
                  ]
                : coOtherLiabilitiesMapped,
            otherliabilitiesType: coBorrowerDetails.otherliabilitiesType,
          },
        };
        dispatch(
          addStepsData({
            ...others,
            formFour: savedData,
          })
        );
        dispatch(nextStep());
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "yourDetails.otherLiabilities",
  });
  const {
    fields: cofields,
    append: coappend,
    remove: coremove,
  } = useFieldArray({
    control,
    name: "coBorrowerDetails.otherLiabilities",
  });

  useEffect(() => {
    if (formFour && Object.keys(formFour).length > 0) {
      // reset(formFour);
      reset({
        yourDetails: {
          ...formFour.yourDetails,
          otherLiabilities:
            formFour.yourDetails?.otherliabilitiesType === "no"
              ? [{ name: "", balanceOwing: "", minMonthlyPayment: "" }]
              : formFour.yourDetails.otherLiabilities || [
                  { name: "", balanceOwing: "", minMonthlyPayment: "" },
                ],
        },
        coBorrowerDetails: {
          ...formFour.coBorrowerDetails,
          otherLiabilities:
            formFour.coBorrowerDetails?.otherliabilitiesType === "no"
              ? [{ name: "", balanceOwing: "", minMonthlyPayment: "" }]
              : formFour.coBorrowerDetails.otherLiabilities || [
                  { name: "", balanceOwing: "", minMonthlyPayment: "" },
                ],
        },
      });
    }
  }, [formFour, reset]);

  return (
    <>
      <ProgressBar progress={60} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[50%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Other Liabilities" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-8 flex-col md:flex-col xl:flex-row sm:gap-4">
            <div className="flex-1">
              <InfoTitle title="Your Details" />
              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                  Do you have any other liabilities ?
                </h6>
                <div className="flex gap-5">
                  <Controller
                    name="yourDetails.otherliabilitiesType"
                    control={control}
                    rules={{ required: "Please select an option" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex gap-5">
                        <RadioButton
                          id="otherliabilities_yes"
                          name="yourDetails.otherliabilitiesType"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="otherliabilities_no"
                          name="yourDetails.otherliabilitiesType"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors?.yourDetails?.otherliabilitiesType && (
                  <ErrorsMessage
                    title={errors.yourDetails.otherliabilitiesType.message}
                    className="!text-left"
                  />
                )}
              </div>

              {otherliabilitiesValue === "yes" && (
                <>
                  <DynamicOtherLiabilities
                    control={control}
                    errors={errors}
                    fields={fields}
                    deleteItem={deleteItem}
                    otherLiabilities="yourDetails.otherLiabilities"
                    remove={remove}
                    applicantID={savedIds?.applicantID as string}
                    deleteIsPending={deleteIsPending}
                  />
                  {fields.length < 3 && (
                    <div className="flex items-center justify-end mt-6  mb-6 sm:mb-2">
                      <AddMore
                        onClick={() =>
                          append({
                            name: "",
                            balanceOwing: "",
                            minMonthlyPayment: "",
                          })
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex-1">
              <InfoTitle title="Co-borrower" />
              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                  Do you have any other liabilities ?
                </h6>
                <div className="flex gap-5">
                  <Controller
                    name="coBorrowerDetails.otherliabilitiesType"
                    control={control}
                    rules={{ required: "Please select an option" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex gap-5">
                        <RadioButton
                          id="cootherliabilities_yes"
                          name="coBorrowerDetails.otherliabilitiesType"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="cootherliabilities_no"
                          name="coBorrowerDetails.otherliabilitiesType"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors?.coBorrowerDetails?.otherliabilitiesType && (
                  <ErrorsMessage
                    title={
                      errors.coBorrowerDetails.otherliabilitiesType.message
                    }
                    className="!text-left"
                  />
                )}
              </div>

              {cootherliabilitiesValue === "yes" && (
                <>
                  <DynamicOtherLiabilities
                    control={control}
                    errors={errors}
                    fields={cofields}
                    deleteItem={deleteItem}
                    otherLiabilities="coBorrowerDetails.otherLiabilities"
                    remove={coremove}
                    applicantID={savedIds?.secondApplicantID as string}
                    deleteIsPending={deleteIsPending}
                  />
                  {cofields.length < 3 && (
                    <div className="flex items-center justify-end mt-6">
                      <AddMore
                        onClick={() =>
                          coappend({
                            name: "",
                            balanceOwing: "",
                            minMonthlyPayment: "",
                          })
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom
              onClick={() => {
                dispatch(prevStep());
              }}
            >
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartFour;
